import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, TextField } from "@mui/material";
import { IoEllipsisHorizontal, IoPencil, IoTrash } from "react-icons/io5";
import { adminColors, TOUCH_HIT_SX, FOCUS_RING_ON_DARK } from "../../theme/adminTheme";
import ConfirmDeleteChatDialog from "../ConfirmDeleteChatDialog";

const ChatSidebarItem = ({ thread, isActive, onSelect, onRename, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(thread.title);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // onDelete resolving after the thread (and this row) is already gone from
  // the list would otherwise warn about setting state on an unmounted
  // component when the delete succeeds.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const openMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  const startRename = (e) => {
    e.stopPropagation();
    setEditValue(thread.title);
    setIsEditing(true);
    closeMenu();
  };

  // Waits for the rename to actually save before closing the editor, so a
  // failed request (bad connection, server error) doesn't silently discard
  // what the student typed - the field stays open with their text and
  // useChatThreads has already shown an error toast explaining why.
  const commitRename = async () => {
    if (isSaving) return;
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === thread.title) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    const updated = await onRename(thread.id, trimmed);
    setIsSaving(false);
    if (updated) {
      setIsEditing(false);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    closeMenu();
    setConfirmDeleteOpen(true);
  };

  // The dialog is a portal, but React still bubbles its synthetic events up
  // through the *component* tree it was rendered from - i.e. through this
  // row's own onClick (the same reason startRename/handleDeleteClick above
  // stop propagation for the menu). Without stopping it here, confirming or
  // cancelling the delete (or even dismissing via the backdrop) would also
  // fire onSelect(thread.id) on the row underneath.
  const handleCancelDelete = (e) => {
    e?.stopPropagation?.();
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async (e) => {
    e?.stopPropagation?.();
    setIsDeleting(true);
    await onDelete(thread.id);
    if (isMountedRef.current) {
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          disabled={isSaving}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setIsEditing(false);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            "& .MuiOutlinedInput-root": { fontSize: "13.5px" },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Open chat: ${thread.title}`}
      aria-current={isActive ? "true" : undefined}
      onClick={() => onSelect(thread.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(thread.id);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 1,
        mb: 0.25,
        minHeight: 44,
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: isActive ? adminColors.accent : "transparent",
        boxShadow: isActive ? "0 2px 8px rgba(79,70,229,0.35)" : "none",
        transition: "background-color 0.15s ease, color 0.15s ease",
        "&:hover": {
          backgroundColor: isActive ? adminColors.accentDark : "rgba(255,255,255,0.06)",
        },
        "&:hover .chat-thread-menu-btn": { opacity: 1 },
        "&:focus-within .chat-thread-menu-btn": { opacity: 1 },
        // Hover-to-reveal has no touch equivalent, so the rename/delete
        // trigger would otherwise be unreachable on phones and tablets.
        "@media (hover: none)": {
          ".chat-thread-menu-btn": { opacity: 1 },
        },
        "&:focus-visible": {
          outline: `2px solid ${FOCUS_RING_ON_DARK}`,
          outlineOffset: 2,
        },
      }}
    >
      <Typography
        noWrap
        sx={{
          flex: 1,
          fontSize: "13.5px",
          fontWeight: isActive ? 600 : 400,
          color: isActive ? "white" : "rgba(255,255,255,0.72)",
        }}
      >
        {thread.title}
      </Typography>
      <IconButton
        size="small"
        className="chat-thread-menu-btn"
        onClick={openMenu}
        aria-label="Chat options"
        sx={{
          opacity: 0,
          transition: "opacity 0.15s",
          color: isActive ? "white" : "rgba(255,255,255,0.5)",
          p: 0.5,
          ...TOUCH_HIT_SX,
        }}
      >
        <IoEllipsisHorizontal size={16} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={startRename}>
          <ListItemIcon>
            <IoPencil size={16} />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <IoTrash size={16} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <ConfirmDeleteChatDialog
        open={confirmDeleteOpen}
        chatTitle={thread.title}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </Box>
  );
};

export default ChatSidebarItem;
